<template>
    <div class="student-management">
        <!-- 筛选器 -->
        <el-card class="filter-card">
            <el-form :inline="true" :model="filters" class="filter-form">
                <el-form-item label="学期">
                    <el-select v-model="filters.semesterId" placeholder="选择学期" @change="fetchStudents"
                        style="width: 200px">
                        <el-option v-for="s in semesters" :key="s.id" :label="s.name" :value="s.id" />
                    </el-select>
                </el-form-item>
                <el-form-item label="年级">
                    <el-select v-model="filters.grade" placeholder="全部年级" clearable @change="fetchStudents"
                        style="width: 100px">
                        <el-option label="高一" value="高一" />
                        <el-option label="高二" value="高二" />
                        <el-option label="高三" value="高三" />
                    </el-select>
                </el-form-item>
                <el-form-item label="班级">
                    <el-input v-model="filters.class_name" placeholder="输入班级" clearable @change="fetchStudents"
                        style="width: 100px" />
                </el-form-item>
                <el-form-item>
                    <el-button type="primary" @click="showAddDialog">
                        <el-icon>
                            <Plus />
                        </el-icon>
                        添加学生
                    </el-button>
                    <el-button @click="showBatchImportDialog">
                        <el-icon>
                            <Upload />
                        </el-icon>
                        批量导入
                    </el-button>
                    <el-button @click="showPromoteDialog" type="warning">
                        <el-icon>
                            <Top />
                        </el-icon>
                        批量升级
                    </el-button>
                </el-form-item>
            </el-form>
        </el-card>

        <!-- 学生列表 -->
        <el-card class="table-card">
            <template #header>
                <div class="card-header">
                    <span>学生列表 ({{ students.length }} 人)</span>
                </div>
            </template>

            <el-table :data="students" v-loading="loading" stripe>
                <el-table-column prop="student_id" label="学号" width="120" />
                <el-table-column prop="name" label="姓名" width="100" />
                <el-table-column prop="class_name" label="班级" width="100" />
                <el-table-column prop="grade" label="年级" width="80">
                    <template #default="{ row }">
                        <el-tag :type="getGradeType(row.grade)" size="small">{{ row.grade || '-' }}</el-tag>
                    </template>
                </el-table-column>
                <el-table-column label="选科情况" min-width="200">
                    <template #default="{ row }">
                        <div v-if="row.selected_subjects" class="subject-tags">
                            <el-tag v-for="subject in parseSubjects(row.selected_subjects)" :key="subject" size="small"
                                type="success" class="subject-tag">
                                {{ subject }}
                            </el-tag>
                        </div>
                        <span v-else-if="row.grade === '高一'" class="no-subject">高一暂不选科</span>
                        <el-button v-else size="small" type="primary" text @click="openSubjectSelect(row)">
                            设置选科
                        </el-button>
                    </template>
                </el-table-column>
                <el-table-column label="操作" width="180" fixed="right">
                    <template #default="{ row }">
                        <el-button text type="primary" @click="editStudent(row)">编辑</el-button>
                        <el-button text type="primary" @click="openSubjectSelect(row)">选科</el-button>
                        <el-popconfirm title="确定删除此学生？" @confirm="deleteStudent(row.id)">
                            <template #reference>
                                <el-button text type="danger">删除</el-button>
                            </template>
                        </el-popconfirm>
                    </template>
                </el-table-column>
            </el-table>
        </el-card>

        <!-- 添加/编辑学生对话框 -->
        <el-dialog v-model="studentDialogVisible" :title="isEdit ? '编辑学生' : '添加学生'" width="500px">
            <el-form :model="studentForm" label-width="100px" :rules="studentRules" ref="studentFormRef">
                <el-form-item label="学号" prop="student_id">
                    <el-input v-model="studentForm.student_id" placeholder="请输入学号" />
                </el-form-item>
                <el-form-item label="姓名" prop="name">
                    <el-input v-model="studentForm.name" placeholder="请输入姓名" />
                </el-form-item>
                <el-form-item label="班级">
                    <el-input v-model="studentForm.class_name" placeholder="如: 1班、2班" />
                </el-form-item>
                <el-form-item label="年级" prop="grade" v-if="!isEdit">
                    <el-select v-model="studentForm.grade" placeholder="选择年级" style="width: 100%">
                        <el-option label="高一" value="高一" />
                        <el-option label="高二" value="高二" />
                        <el-option label="高三" value="高三" />
                    </el-select>
                </el-form-item>
                <el-form-item label="选科" v-if="studentForm.grade !== '高一'">
                    <el-checkbox-group v-model="studentForm.selectedSubjectsList" :max="3">
                        <el-checkbox v-for="s in allElectiveSubjects" :key="s" :label="s">{{ s }}</el-checkbox>
                    </el-checkbox-group>
                    <div class="subject-hint">
                        <el-text type="info" size="small">上海3+3模式：从6门小科中选择3门作为等级考科目</el-text>
                    </div>
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="studentDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitStudent" :loading="submitting">确定</el-button>
            </template>
        </el-dialog>

        <!-- 选科设置对话框 -->
        <el-dialog v-model="subjectDialogVisible" title="设置选科" width="500px">
            <div class="subject-select-header" v-if="currentStudent">
                <span>学生：{{ currentStudent.name }} ({{ currentStudent.student_id }})</span>
            </div>
            <el-alert type="info" :closable="false" class="subject-alert">
                <template #title>上海高考"3+3"模式</template>
                <template #default>
                    <ul>
                        <li>语数外为必考科目</li>
                        <li>从物理、化学、生物、政治、历史、地理6门中选择3门作为等级考</li>
                        <li>其余3门为合格考（高二下学期完成）</li>
                    </ul>
                </template>
            </el-alert>
            <div class="subject-select-area">
                <div class="subject-title">请选择3门等级考科目：</div>
                <el-checkbox-group v-model="selectedSubjectsList" :max="3" class="subject-checkbox-group">
                    <el-checkbox v-for="s in allElectiveSubjects" :key="s" :label="s" class="subject-checkbox">
                        {{ s }}
                    </el-checkbox>
                </el-checkbox-group>
                <div class="selection-summary">
                    <div class="summary-item">
                        <span class="label">等级考（已选 {{ selectedSubjectsList.length }}/3）：</span>
                        <el-tag v-for="s in selectedSubjectsList" :key="s" type="success" size="small"
                            class="summary-tag">{{ s
                            }}</el-tag>
                        <span v-if="selectedSubjectsList.length === 0" class="empty">未选择</span>
                    </div>
                    <div class="summary-item">
                        <span class="label">合格考：</span>
                        <el-tag v-for="s in passSubjects" :key="s" type="info" size="small" class="summary-tag">{{ s
                            }}</el-tag>
                    </div>
                </div>
            </div>
            <template #footer>
                <el-button @click="subjectDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="saveSubjectSelection" :loading="submitting"
                    :disabled="selectedSubjectsList.length !== 3">
                    保存选科
                </el-button>
            </template>
        </el-dialog>

        <!-- 批量升级对话框 -->
        <el-dialog v-model="promoteDialogVisible" title="批量升级学生" width="500px">
            <el-alert type="warning" :closable="false" style="margin-bottom: 16px">
                将原学期的学生升级到新学期（高一→高二→高三），高三学生将毕业不再升级
            </el-alert>
            <el-form :model="promoteForm" label-width="100px">
                <el-form-item label="原学期" required>
                    <el-select v-model="promoteForm.from_semester_id" placeholder="选择原学期" style="width: 100%">
                        <el-option v-for="s in semesters" :key="s.id" :label="s.name" :value="s.id" />
                    </el-select>
                </el-form-item>
                <el-form-item label="目标学期" required>
                    <el-select v-model="promoteForm.to_semester_id" placeholder="选择目标学期" style="width: 100%">
                        <el-option v-for="s in semesters" :key="s.id" :label="s.name" :value="s.id" />
                    </el-select>
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="promoteDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="promoteStudents" :loading="submitting">确认升级</el-button>
            </template>
        </el-dialog>

        <!-- 批量导入学生对话框 -->
        <el-dialog v-model="batchImportDialogVisible" title="批量导入学生" width="600px">
            <el-form :model="batchImportForm" label-width="100px">
                <el-form-item label="学期" required>
                    <el-select v-model="batchImportForm.semesterId" placeholder="选择学期" style="width: 100%">
                        <el-option v-for="s in semesters" :key="s.id" :label="s.name" :value="s.id" />
                    </el-select>
                </el-form-item>
                <el-form-item label="年级" required>
                    <el-select v-model="batchImportForm.grade" placeholder="选择年级" style="width: 100%">
                        <el-option label="高一" value="高一" />
                        <el-option label="高二" value="高二" />
                        <el-option label="高三" value="高三" />
                    </el-select>
                </el-form-item>
            </el-form>

            <el-tabs v-model="batchImportTab" class="import-tabs">
                <!-- Excel 导入 -->
                <el-tab-pane label="Excel导入" name="excel">
                    <div class="import-section">
                        <el-button type="primary" plain @click="downloadTemplate" class="download-btn">
                            <el-icon>
                                <Download />
                            </el-icon>
                            下载模板
                        </el-button>
                        <el-upload ref="uploadRef" :auto-upload="false" :limit="1" accept=".xlsx,.xls,.csv"
                            :on-change="handleFileChange" :on-remove="() => importFile = null" drag class="upload-area">
                            <el-icon class="el-icon--upload">
                                <Upload />
                            </el-icon>
                            <div class="el-upload__text">
                                拖拽文件到此处，或 <em>点击上传</em>
                            </div>
                            <template #tip>
                                <div class="el-upload__tip">
                                    Excel需包含"学号"和"姓名"列，可选"班级"列
                                </div>
                            </template>
                        </el-upload>
                    </div>
                </el-tab-pane>

                <!-- 粘贴导入 -->
                <el-tab-pane label="粘贴导入" name="paste">
                    <div class="import-section">
                        <el-alert type="info" :closable="false" class="paste-tip">
                            <template #title>格式说明</template>
                            <template #default>
                                每行一个学生，使用Tab或逗号分隔<br />
                                格式：学号, 姓名, 班级（可选）<br />
                                例如：2025001, 张三, 1班
                            </template>
                        </el-alert>
                        <el-input v-model="batchImportForm.pasteContent" type="textarea" :rows="8"
                            placeholder="在此粘贴学生数据..." class="paste-textarea" />
                        <div class="paste-preview" v-if="parsedStudents.length > 0">
                            <el-tag type="success">已解析 {{ parsedStudents.length }} 名学生</el-tag>
                            <el-table :data="parsedStudents.slice(0, 5)" size="small" max-height="150"
                                style="margin-top: 8px">
                                <el-table-column prop="student_id" label="学号" />
                                <el-table-column prop="name" label="姓名" />
                                <el-table-column prop="class_name" label="班级" />
                            </el-table>
                            <div v-if="parsedStudents.length > 5" class="more-hint">
                                ... 还有 {{ parsedStudents.length - 5 }} 条数据
                            </div>
                        </div>
                    </div>
                </el-tab-pane>
            </el-tabs>

            <template #footer>
                <el-button @click="batchImportDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="doBatchImport" :loading="importing">导入</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { Plus, Upload, Top, Download } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { semesterApi, studentApi } from '@/api'

// 状态
const loading = ref(false)
const submitting = ref(false)
const importing = ref(false)
const semesters = ref([])
const students = ref([])

// 6门选考科目
const allElectiveSubjects = ['物理', '化学', '生物', '政治', '历史', '地理']

// 筛选条件
const filters = reactive({
    semesterId: null,
    grade: null,
    class_name: ''
})

// 学生表单
const studentDialogVisible = ref(false)
const isEdit = ref(false)
const editingStudentId = ref(null)
const studentFormRef = ref(null)
const studentForm = reactive({
    student_id: '',
    name: '',
    class_name: '',
    grade: '高一',
    selectedSubjectsList: []
})
const studentRules = {
    student_id: [{ required: true, message: '请输入学号', trigger: 'blur' }],
    name: [{ required: true, message: '请输入姓名', trigger: 'blur' }],
    grade: [{ required: true, message: '请选择年级', trigger: 'change' }]
}

// 选科对话框
const subjectDialogVisible = ref(false)
const currentStudent = ref(null)
const selectedSubjectsList = ref([])

// 计算合格考科目
const passSubjects = computed(() => {
    return allElectiveSubjects.filter(s => !selectedSubjectsList.value.includes(s))
})

// 升级对话框
const promoteDialogVisible = ref(false)
const promoteForm = reactive({
    from_semester_id: null,
    to_semester_id: null
})

// 批量导入对话框
const batchImportDialogVisible = ref(false)
const batchImportTab = ref('excel')
const uploadRef = ref(null)
const importFile = ref(null)
const batchImportForm = reactive({
    semesterId: null,
    grade: '高一',
    pasteContent: ''
})

// 解析粘贴内容
const parsedStudents = computed(() => {
    if (!batchImportForm.pasteContent.trim()) return []
    const lines = batchImportForm.pasteContent.trim().split('\n')
    const result = []
    for (const line of lines) {
        const trimmed = line.trim()
        if (!trimmed) continue
        const parts = trimmed.includes('\t') ? trimmed.split('\t') : trimmed.split(/[,，]/)
        if (parts.length >= 2) {
            result.push({
                student_id: parts[0].trim(),
                name: parts[1].trim(),
                class_name: parts[2]?.trim() || ''
            })
        }
    }
    return result
})

// 辅助方法
const getGradeType = (grade) => {
    const types = { '高一': 'success', '高二': 'warning', '高三': 'danger' }
    return types[grade] || 'info'
}

const parseSubjects = (subjectsStr) => {
    if (!subjectsStr) return []
    return subjectsStr.split(',').map(s => s.trim()).filter(s => s)
}

// 数据获取
const fetchSemesters = async () => {
    try {
        semesters.value = await semesterApi.getAll()
        if (semesters.value.length > 0 && !filters.semesterId) {
            const current = semesters.value.find(s => s.is_active)
            filters.semesterId = current ? current.id : semesters.value[0].id
        }
    } catch (e) {
        console.error(e)
    }
}

const fetchStudents = async () => {
    if (!filters.semesterId) return
    loading.value = true
    try {
        const params = { semester_id: filters.semesterId }
        if (filters.grade) params.grade = filters.grade
        if (filters.class_name) params.class_name = filters.class_name
        students.value = await studentApi.getAll(params)
    } catch (e) {
        console.error(e)
    } finally {
        loading.value = false
    }
}

// 学生操作
const showAddDialog = () => {
    isEdit.value = false
    editingStudentId.value = null
    Object.assign(studentForm, {
        student_id: '',
        name: '',
        class_name: '',
        grade: '高一',
        selectedSubjectsList: []
    })
    studentDialogVisible.value = true
}

const editStudent = (row) => {
    isEdit.value = true
    editingStudentId.value = row.id
    Object.assign(studentForm, {
        student_id: row.student_id,
        name: row.name,
        class_name: row.class_name,
        grade: row.grade,
        selectedSubjectsList: parseSubjects(row.selected_subjects)
    })
    studentDialogVisible.value = true
}

const submitStudent = async () => {
    if (!studentFormRef.value) return
    await studentFormRef.value.validate(async (valid) => {
        if (!valid) return
        submitting.value = true
        try {
            const selectedSubjects = studentForm.selectedSubjectsList.length > 0
                ? studentForm.selectedSubjectsList.join(',')
                : null

            if (isEdit.value) {
                await studentApi.update(editingStudentId.value, {
                    student_id: studentForm.student_id,
                    name: studentForm.name,
                    class_name: studentForm.class_name,
                    selected_subjects: selectedSubjects
                })
                ElMessage.success('学生信息更新成功')
            } else {
                const student = await studentApi.create({
                    student_id: studentForm.student_id,
                    name: studentForm.name,
                    class_name: studentForm.class_name,
                    selected_subjects: selectedSubjects
                })
                await studentApi.setGrade(student.id, {
                    semester_id: filters.semesterId,
                    grade: studentForm.grade
                })
                ElMessage.success('学生添加成功')
            }
            studentDialogVisible.value = false
            fetchStudents()
        } catch (e) {
            console.error(e)
        } finally {
            submitting.value = false
        }
    })
}

const deleteStudent = async (id) => {
    try {
        await studentApi.delete(id)
        ElMessage.success('学生已删除')
        fetchStudents()
    } catch (e) {
        console.error(e)
    }
}

// 选科操作
const openSubjectSelect = (row) => {
    currentStudent.value = row
    selectedSubjectsList.value = parseSubjects(row.selected_subjects)
    subjectDialogVisible.value = true
}

const saveSubjectSelection = async () => {
    if (!currentStudent.value) return
    if (selectedSubjectsList.value.length !== 3) {
        ElMessage.warning('请选择3门等级考科目')
        return
    }

    submitting.value = true
    try {
        await studentApi.update(currentStudent.value.id, {
            selected_subjects: selectedSubjectsList.value.join(',')
        })
        ElMessage.success('选科保存成功')
        subjectDialogVisible.value = false
        fetchStudents()
    } catch (e) {
        console.error(e)
    } finally {
        submitting.value = false
    }
}

// 批量导入操作
const showBatchImportDialog = () => {
    batchImportForm.semesterId = filters.semesterId
    batchImportForm.grade = filters.grade || '高一'
    batchImportForm.pasteContent = ''
    importFile.value = null
    batchImportTab.value = 'excel'
    batchImportDialogVisible.value = true
}

const handleFileChange = (file) => {
    importFile.value = file.raw
}

const downloadTemplate = () => {
    const csvContent = '学号,姓名,班级\n2025001,张三,1班\n2025002,李四,2班'
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = '学生导入模板.csv'
    link.click()
    window.URL.revokeObjectURL(url)
}

const doBatchImport = async () => {
    if (!batchImportForm.semesterId) {
        ElMessage.warning('请选择导入到的学期')
        return
    }
    if (!batchImportForm.grade) {
        ElMessage.warning('请选择导入到的年级')
        return
    }

    if (batchImportTab.value === 'paste') {
        await importByPaste()
    } else {
        await importByFile()
    }
}

const importByPaste = async () => {
    if (parsedStudents.value.length === 0) {
        ElMessage.warning('没有解析到有效的学生数据')
        return
    }

    importing.value = true
    let successCount = 0
    let failCount = 0

    try {
        for (const stu of parsedStudents.value) {
            try {
                const student = await studentApi.create({
                    student_id: stu.student_id,
                    name: stu.name,
                    class_name: stu.class_name
                })
                await studentApi.setGrade(student.id, {
                    semester_id: batchImportForm.semesterId,
                    grade: batchImportForm.grade
                })
                successCount++
            } catch (e) {
                failCount++
                console.error('导入学生失败:', stu.student_id, e)
            }
        }

        if (successCount > 0) {
            ElMessage.success(`成功导入 ${successCount} 名学生${failCount > 0 ? `，${failCount} 名失败` : ''}`)
            batchImportDialogVisible.value = false
            fetchStudents()
        } else {
            ElMessage.error('所有学生导入失败，请检查数据格式或学号是否重复')
        }
    } finally {
        importing.value = false
    }
}

const importByFile = async () => {
    if (!importFile.value) {
        ElMessage.warning('请选择文件')
        return
    }

    importing.value = true
    try {
        const result = await studentApi.batchImport(
            batchImportForm.semesterId,
            batchImportForm.grade,
            importFile.value
        )
        ElMessage.success(result.message)
        batchImportDialogVisible.value = false
        fetchStudents()
    } catch (e) {
        console.error(e)
    } finally {
        importing.value = false
    }
}

// 升级操作
const showPromoteDialog = () => {
    promoteForm.from_semester_id = null
    promoteForm.to_semester_id = null
    promoteDialogVisible.value = true
}

const promoteStudents = async () => {
    if (!promoteForm.from_semester_id || !promoteForm.to_semester_id) {
        ElMessage.warning('请选择原学期和目标学期')
        return
    }
    if (promoteForm.from_semester_id === promoteForm.to_semester_id) {
        ElMessage.warning('原学期和目标学期不能相同')
        return
    }

    submitting.value = true
    try {
        const result = await studentApi.promote({
            from_semester_id: promoteForm.from_semester_id,
            to_semester_id: promoteForm.to_semester_id,
            exclude_student_ids: []
        })
        ElMessage.success(result.message)
        promoteDialogVisible.value = false
        fetchStudents()
    } catch (e) {
        console.error(e)
    } finally {
        submitting.value = false
    }
}

// 初始化
onMounted(async () => {
    await fetchSemesters()
    fetchStudents()
})
</script>

<style scoped>
.student-management {
    max-width: 1400px;
}

.filter-card {
    margin-bottom: 16px;
}

.filter-form {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.table-card {
    margin-bottom: 16px;
}

.card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.card-header span {
    font-size: 16px;
    font-weight: 500;
}

.subject-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}

.subject-tag {
    margin-right: 4px;
}

.no-subject {
    color: #909399;
    font-size: 12px;
}

.subject-hint {
    margin-top: 8px;
}

.subject-select-header {
    margin-bottom: 16px;
    padding: 12px;
    background: #f5f7fa;
    border-radius: 8px;
    font-weight: 500;
}

.subject-alert {
    margin-bottom: 16px;
}

.subject-select-area {
    padding: 16px;
    background: #fafafa;
    border-radius: 8px;
}

.subject-title {
    font-weight: 500;
    margin-bottom: 12px;
}

.subject-checkbox-group {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 16px;
}

.subject-checkbox {
    width: calc(33% - 8px);
    margin: 0 !important;
    padding: 8px 12px;
    border: 1px solid #dcdfe6;
    border-radius: 6px;
    background: white;
}

.subject-checkbox:hover {
    border-color: #409eff;
}

.selection-summary {
    padding-top: 16px;
    border-top: 1px solid #e5e7eb;
}

.summary-item {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 8px;
}

.summary-item .label {
    color: #606266;
    min-width: 140px;
}

.summary-tag {
    margin-right: 4px;
}

.empty {
    color: #909399;
    font-size: 12px;
}

/* 导入对话框样式 */
.import-tabs {
    margin-top: 16px;
}

.import-section {
    padding: 8px 0;
}

.download-btn {
    margin-bottom: 16px;
}

.upload-area {
    width: 100%;
}

.paste-tip {
    margin-bottom: 12px;
}

.paste-textarea {
    margin-top: 12px;
}

.paste-preview {
    margin-top: 12px;
    padding: 12px;
    background: #f9fafb;
    border-radius: 8px;
}

.more-hint {
    margin-top: 8px;
    color: #909399;
    font-size: 12px;
}
</style>
